/**
 * @fileoverview Super-admin audit log viewer page.
 *
 * Read-only viewer over the append-only `audit_log` table. Renders a filter
 * form + a paginated results table. All styling uses Seedling tokens; table
 * is wrapped in `.tbl-wrap` per CLAUDE.md; IDs use the mono font.
 *
 * Mounted under `${adminPath}/audit-log` so the super-admin IP whitelist +
 * role gate fronting the admin secret path applies transparently.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Pagination from '../components/Pagination';

const DEFAULT_PER_PAGE = 50;

const ACTION_OPTIONS = [
  'auth.login_success', 'auth.login_failure', 'auth.logout',
  'auth.password_reset_request', 'auth.password_reset_complete',
  'auth.email_verification',
  'admin.org_settings_update', 'admin.member_role_change',
  'admin.invitation_send', 'admin.invitation_revoke',
  'admin.super_admin_action',
  'invoice.create', 'invoice.update', 'invoice.delete',
  'vendor.create', 'vendor.update', 'vendor.delete',
  'category.create', 'category.update', 'category.delete',
  'business.create', 'business.update', 'business.delete',
  'sku.create', 'sku.update', 'sku.delete',
  'line_item.update',
  'budget.create', 'budget.update', 'budget.delete',
  'export.excel', 'export.csv', 'export.json',
  'ocr.batch_start', 'ocr.batch_complete',
];

const RESOURCE_TYPE_OPTIONS = [
  'invoice', 'vendor', 'category', 'business', 'sku',
  'line_item', 'budget', 'organization', 'user', 'export', 'ocr',
];

const EMPTY_FILTERS = {
  user_id: '',
  user_search: '',
  action: '',
  resource_type: '',
  date_from: '',
  date_to: '',
  result: '',
};

/**
 * Strip empty-string filter values so the backend only sees applied filters.
 *
 * @param {Object} filters - Raw filter state from the form.
 * @returns {Object} Filters with empty strings removed.
 */
function compactFilters(filters) {
  const out = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) out[k] = v;
  });
  return out;
}

/**
 * Super-admin audit log viewer.
 *
 * @returns {JSX.Element}
 */
function AuditLogViewer() {
  const { adminApiPrefix, isSuperAdmin } = useAuth();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const headingRef = useRef(null);

  // Focus heading on mount for screen-reader / keyboard users (CLAUDE.md rule).
  useEffect(() => {
    if (headingRef.current) {
      headingRef.current.focus();
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    if (!adminApiPrefix) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...compactFilters(appliedFilters),
        page,
        per_page: perPage,
      };
      const resp = await api.getAuditLog(adminApiPrefix, params);
      const body = resp?.data || resp || {};
      setEntries(body.entries || []);
      setTotal(body.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load audit log');
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [adminApiPrefix, appliedFilters, page, perPage]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  // Super-admin guard — component is already mounted behind a super-admin
  // ProtectedRoute at the App level, but render-level defense in depth.
  if (isSuperAdmin === false) {
    return (
      <div className="page">
        <h1>Audit Log</h1>
        <div className="empty-state" role="alert">
          <div className="empty-state__title">Access denied</div>
          <p className="empty-state__description">
            This page is restricted to system administrators.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page audit-log-viewer">
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="audit-log-viewer__heading"
      >
        Audit Log
      </h1>
      <p className="audit-log-viewer__subtitle">
        Read-only view of the append-only security audit trail.
      </p>

      <form
        className="audit-log-viewer__filters"
        onSubmit={handleApply}
        aria-label="Audit log filters"
      >
        <div className="field">
          <label className="label" htmlFor="audit-user-id">User ID</label>
          <input
            id="audit-user-id"
            className="input"
            type="number"
            name="user_id"
            value={filters.user_id}
            onChange={handleFilterChange}
            min="1"
            placeholder="e.g. 42"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="audit-user-search">User contains</label>
          <input
            id="audit-user-search"
            className="input"
            type="text"
            name="user_search"
            value={filters.user_search}
            onChange={handleFilterChange}
            placeholder="name, username, or email"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="audit-action">Action</label>
          <select
            id="audit-action"
            className="input"
            name="action"
            value={filters.action}
            onChange={handleFilterChange}
          >
            <option value="">Any action</option>
            {ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="audit-resource-type">Resource Type</label>
          <select
            id="audit-resource-type"
            className="input"
            name="resource_type"
            value={filters.resource_type}
            onChange={handleFilterChange}
          >
            <option value="">Any resource</option>
            {RESOURCE_TYPE_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="audit-date-from">From</label>
          <input
            id="audit-date-from"
            className="input"
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={handleFilterChange}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="audit-date-to">To</label>
          <input
            id="audit-date-to"
            className="input"
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={handleFilterChange}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="audit-result">Result</label>
          <select
            id="audit-result"
            className="input"
            name="result"
            value={filters.result}
            onChange={handleFilterChange}
          >
            <option value="">Any</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
          </select>
        </div>

        <div className="audit-log-viewer__actions">
          <button type="submit" className="btn btn-primary">Apply</button>
          <button type="button" className="btn btn-ghost" onClick={handleReset}>
            Reset
          </button>
        </div>
      </form>

      {error && (
        <div className="audit-log-viewer__error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="audit-log-viewer__loading" role="status" aria-live="polite">
          <img
            src="/logos/loader/cogslog-loader-light-md.svg"
            width="48"
            height="108"
            alt="Loading audit log..."
          />
        </div>
      ) : entries.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No audit entries match your filters</div>
          <p className="empty-state__description">
            Adjust the filters above or click Reset to see all entries.
          </p>
        </div>
      ) : (
        <>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col" aria-sort="descending">Occurred At</th>
                  <th scope="col">User</th>
                  <th scope="col">Action</th>
                  <th scope="col">Resource Type</th>
                  <th scope="col">Resource ID</th>
                  <th scope="col">Result</th>
                  <th scope="col">IP</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  // Prefer display_name, then username, then 'System' when
                  // the row has no user (background tasks / CLI). Show email
                  // as a muted subtitle so operators can copy-paste it.
                  const primary =
                    e.user_display_name || e.user_username || (e.user_id == null ? 'System' : `#${e.user_id}`);
                  const subtitle = e.user_email || (e.user_id != null && (e.user_display_name || e.user_username)
                    ? `#${e.user_id}`
                    : '');
                  return (
                    <tr key={e.id}>
                      <td className="audit-log-viewer__cell--mono">
                        {e.occurred_at || ''}
                      </td>
                      <td className="audit-log-viewer__user-cell">
                        <div className="audit-log-viewer__user-primary">{primary}</div>
                        {subtitle && (
                          <div className="audit-log-viewer__user-subtitle">{subtitle}</div>
                        )}
                      </td>
                      <td>{e.action}</td>
                      <td>{e.resource_type || ''}</td>
                      <td className="audit-log-viewer__cell--mono">
                        {e.resource_id ?? ''}
                      </td>
                      <td>{e.result}</td>
                      <td className="audit-log-viewer__cell--mono">
                        {e.ip_address || ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {total > perPage && (
            <Pagination
              totalItems={total}
              currentPage={page}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
              perPageOptions={[25, 50, 100, 200]}
            />
          )}
        </>
      )}
    </div>
  );
}

export default AuditLogViewer;
